import { useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { useForm, FormProvider } from 'react-hook-form';

import { formatDateAsAnniversary, formatDateForDisplay } from 'helpers/dateTimes';

import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { useGenerateQuantitiesMutation, useGetSuppliersQuery, TotadminPermission } from 'data/__generated__';
import { SiteDetailFormCheckboxes } from './components/SiteDetailFormCheckboxes';
import { TotemCheckbox } from 'components/TotemCheckbox';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';

type FormData = {
    selectedSiteIds: string[];
    selectedSupplierIds: string[];
};

export function GenerateQuantitiesButton({ date }: { date: Date }) {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [generateQuantities, { loading }] = useGenerateQuantitiesMutation();
    const [reGenerateMenuFromZero, setReGenerateMenuFromZero] = useState<boolean>(false);
    const methods = useForm<FormData>({
        defaultValues: {
            selectedSiteIds: [],
            selectedSupplierIds: [],
        },
    });

    const {
        loading: suppliersLoading,
        data: suppliersData,
        error: suppliersError,
    } = useGetSuppliersQuery({
        fetchPolicy: 'cache-and-network',
    });

    if (!suppliersLoading && (suppliersError || !suppliersData)) {
        throw new Error('Une erreur est survenue lors de la récupération des fournisseurs');
    }

    // TODO: Uncomment when menu can be generated for a specific supplier
    // const supplierOptions = (suppliersData?.suppliers || []).map(({ _id, name }) => ({
    //     label: name,
    //     value: _id,
    // }));

    const onSubmit = methods.handleSubmit(async (formData) => {
        try {
            await generateQuantities({
                variables: {
                    mondayDate: formatDateAsAnniversary({ dateTime: date }),
                    siteIds: formData.selectedSiteIds,
                    supplierIds: formData.selectedSupplierIds,
                    reGenerateMenuFromZero,
                },
                onCompleted: ({ generateQuantities }) => {
                    if (generateQuantities?.success) {
                        toast.success('La génération des menu & quantités est terminée !');
                        setIsOpen(false);
                    } else {
                        toast.error(`Une erreur s'est produite : ${generateQuantities?.error || 'erreur inconnue'}`);
                    }
                },
                onError: (error) => {
                    toast.error(`Impossible de générer les quantités : ${error}`);
                },
            });
        } catch (error) {
            toast.error(`Impossible de générer les quantités : ${error}`);
            throw Error('Impossible de générer les quantités');
        }
    });

    return (
        <>
            <TotemPopup
                title="Re-génération des quantités"
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                contentOverflow="visible"
            >
                <FormProvider {...methods}>
                    <form onSubmit={onSubmit}>
                        <FormContent>
                            <InfoText>
                                En confirmant vous remplacerez les quantités existantes pour la semaine du{' '}
                                {formatDateForDisplay(date)}.
                                <br />
                                Cette opération peut prendre un certain temps, ne la relancez pas en parallèle ;)
                            </InfoText>
                            <SiteDetailFormCheckboxes label="Site(s) concerné(s)" name="selectedSiteIds" />
                            <TotemCheckbox
                                label="Re-générer le menu de zéro"
                                onChange={() => {
                                    setReGenerateMenuFromZero(!reGenerateMenuFromZero);
                                }}
                                checked={reGenerateMenuFromZero}
                            />
                            {/* TODO: Uncomment when menu can be generated for a specific supplier */}
                            {/* <DetailFormSelect<string>
                                label="Fournisseur(s) (tous par défaut)"
                                placeholder="Sélectionnez les fournisseurs concernés"
                                isClearable
                                name="selectedSupplierIds"
                                options={supplierOptions}
                            /> */}
                            <TotemPrimaryLoadingButton
                                loading={loading}
                                disabled={loading}
                                onClick={onSubmit}
                                label="Confirmer"
                                useIcon={false}
                                requiredPermissions={[TotadminPermission.MenusManage]}
                            >
                                Confirmer
                            </TotemPrimaryLoadingButton>
                        </FormContent>
                    </form>
                </FormProvider>
            </TotemPopup>
            <TotemPrimaryButton
                disabled={loading}
                onClick={() => setIsOpen(true)}
                $minWidth="300px"
                requiredPermissions={[TotadminPermission.MenusManage]}
            >
                Quantités & Menu
            </TotemPrimaryButton>
        </>
    );
}

const FormContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const InfoText = styled.div`
    padding: 20px 0px;
`;
